/**
 * Content Carousel Component
 * Manages a sliding carousel using Swiper.js with centered active slide
 * and animated text overlay.
 */

import type { Magics } from 'alpinejs';
import Alpine from 'alpinejs';
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

interface ContentCarouselData {
    activeIndex: number;
    totalSlides: number;
    swiperInstance: Swiper | null;
    init(this: ContentCarouselContext): void;
    next(this: ContentCarouselContext): void;
    prev(this: ContentCarouselContext): void;
    isActive(index: number): boolean;
    controlVideos(this: ContentCarouselContext): void;
}

type ContentCarouselContext = ContentCarouselData & Magics<ContentCarouselData>;

export default function contentCarousel() {
    return {
        activeIndex: 0,
        totalSlides: 0,
        swiperInstance: null,

        // Initialize Swiper carousel
        init() {
            // Get total slide count from the Liquid template
            const slideCount = this.$el.dataset.slideCount;
            if (slideCount) {
                this.totalSlides = parseInt(slideCount, 10);
            }

            if (this.totalSlides === 0) {
                return;
            }

            // Initialize Swiper instance
            this.$nextTick(() => {
                const swiperContainer = this.$el.querySelector('.swiper') as HTMLElement | null;

                if (!swiperContainer) {
                    return;
                }

                const prevButton = this.$el.querySelector('[data-carousel-prev]') as
                    | HTMLElement
                    | undefined;
                const nextButton = this.$el.querySelector('[data-carousel-next]') as
                    | HTMLElement
                    | undefined;

                const showPagination = this.$el.dataset.showPagination === 'true';
                const autoplay = this.$el.dataset.autoplay === 'true';

                const swiperConfig: any = {
                    modules: [Navigation, Pagination, Autoplay],
                    loop: true,
                    centeredSlides: true,
                    grabCursor: true,
                    navigation: {
                        prevEl: prevButton,
                        nextEl: nextButton,
                    },
                    breakpoints: {
                        640: {
                            spaceBetween: 20,
                            slidesPerView: 1,
                        },
                        1024: {
                            spaceBetween: 32,
                            slidesPerView: 'auto',
                        },
                    },
                    on: {
                        slideChange: () => {
                            if (this.swiperInstance) {
                                this.activeIndex = this.swiperInstance.realIndex;
                                this.controlVideos();
                            }
                        },
                    },
                };

                if (showPagination) {
                    const paginationEl = this.$el.querySelector(
                        '.swiper-pagination',
                    ) as HTMLElement | null;
                    swiperConfig.pagination = {
                        el: paginationEl,
                        clickable: true,
                    };
                }

                if (autoplay) {
                    swiperConfig.autoplay = {
                        delay: 5000,
                        disableOnInteraction: true,
                    };
                }

                this.swiperInstance = new Swiper(swiperContainer, swiperConfig);

                this.$nextTick(() => {
                    this.controlVideos();
                });
            });
        },

        // Navigate to next slide
        next() {
            if (this.swiperInstance) {
                this.swiperInstance.slideNext();
            }
        },

        // Navigate to previous slide
        prev() {
            if (this.swiperInstance) {
                this.swiperInstance.slidePrev();
            }
        },

        // Check if slide at real index is active
        isActive(index: number): boolean {
            return index === this.activeIndex;
        },

        // Control video playback: play active slide, pause all others
        controlVideos() {
            if (!this.swiperInstance) {
                return;
            }

            // Pause all videos
            const allVideos = this.$el.querySelectorAll(
                '[x-data="videoPlayer"]',
            ) as NodeListOf<HTMLElement>;
            allVideos.forEach((videoPlayer: HTMLElement) => {
                const videoComponent = Alpine.$data(videoPlayer);
                if (videoComponent) {
                    videoComponent.pause();
                }
            });

            // Play only the active slide's video
            const activeSlide = this.swiperInstance.slides[this.swiperInstance.activeIndex] as
                | HTMLElement
                | undefined;
            if (activeSlide) {
                const activeVideoPlayer = activeSlide.querySelector(
                    '[x-data="videoPlayer"]',
                ) as HTMLElement | null;
                if (activeVideoPlayer) {
                    const videoComponent = Alpine.$data(activeVideoPlayer);
                    if (videoComponent) {
                        videoComponent.play();
                    }
                }
            }
        },
    } as ContentCarouselData;
}
